import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TreeData } from "@src/components/tree/TreeTypes";
import { SelectedResourceData } from "./classroomSidebarSlice";

interface InitialState {
  parentTree: TreeData[];
  pageTree: TreeData[];
  selectedNode: TreeData | null;
  selectedNodeName: string;
  resourceId: string;
  resourceType: string;
  practiceQExpandedItems: string[];
  practiceQSelectedItemId: string;
  selectedResourceData: SelectedResourceData | null;
}

const initialState: InitialState = {
  parentTree: [],
  pageTree: [],
  selectedNode: null,
  selectedNodeName: "",
  resourceId: "",
  resourceType: "",
  practiceQExpandedItems: [],
  practiceQSelectedItemId: "",
  selectedResourceData: null,
};

const addChildrenRecursively = (
  tree: TreeData[],
  parentId: string,
  children: TreeData[]
): TreeData[] => {
  return tree.map((node) => {
    if (node._id === parentId) {
      return {
        ...node,
        expanded: node.hasChildren ? true : false,
        children: children,
      };
    } else if (node.children) {
      return {
        ...node,
        children: addChildrenRecursively(node.children, parentId, children),
      };
    }
    return node;
  });
};

const toggleExpandedById = (
  arr: TreeData[] | undefined,
  parentId: string | null
): TreeData[] => {
  return arr
    ? arr.map((item) => {
        if (Array.isArray(item.children)) {
          const updatedItem = {
            ...item,
            expanded: item._id === parentId ? !item.expanded : item.expanded,
            children: toggleExpandedById(
              item.children,
              item._id === parentId && !item.expanded ? null : parentId
            ),
          };

          // If parent is collapsed, set all children to false
          if (item._id === parentId && !updatedItem.expanded) {
            updatedItem.children = updatedItem.children.map((child) => ({
              ...child,
              expanded: false,
              children: toggleExpandedById(child.children, null),
            }));
          }

          return updatedItem;
        }
        return item;
      })
    : [];
};

const practiceQSidebarSlice = createSlice({
  name: "practiceQSidebar",
  initialState: initialState,
  reducers: {
    setPageTreeData: (state, action) => {
      state.pageTree = action.payload;
    },
    setParentTreeData: (state, action) => {
      state.parentTree = action.payload;
    },
    updateSelectedParent: (state, action) => {
      state.parentTree = action.payload;
    },
    setSelectedNode: (state, action) => {
      state.selectedNode = action.payload;
    },
    addChildrenToPageTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.pageTree = addChildrenRecursively(
        state.pageTree,
        parentId,
        children
      );
    },
    addChildrenToParentTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.parentTree = addChildrenRecursively(
        state.parentTree,
        parentId,
        children
      );
    },
    collapseExpandInPageTree: (state, action) => {
      state.pageTree = toggleExpandedById(state.pageTree, action.payload._id);
    },
    collapseExpandInParentTree: (state, action) => {
      state.parentTree = toggleExpandedById(
        state.parentTree,
        action.payload._id
      );
    },
    setSelectedNodeName: (state, action) => {
      state.selectedNodeName = action.payload;
    },
    setResourceDataForPracticeQ: (
      state,
      action: PayloadAction<Partial<InitialState>>
    ) => {
      return { ...state, ...action.payload };
    },
    resetPracticeQTree: (state) => {
      state.pageTree = [];
      state.parentTree = [];
      state.selectedNode = null;
      state.practiceQExpandedItems = [];
      state.practiceQSelectedItemId = "";
    },
    setPracticeQSelectedItemId: (state, action) => {
      state.practiceQSelectedItemId = action.payload;
    },
    setPracticeQExpandedItems: (state, action) => {
      state.practiceQExpandedItems = action.payload;
    },
  },
});

export const {
  setPageTreeData,
  updateSelectedParent,
  addChildrenToPageTree,
  addChildrenToParentTree,
  setParentTreeData,
  collapseExpandInParentTree,
  collapseExpandInPageTree,
  setSelectedNode,
  resetPracticeQTree,
  setSelectedNodeName,
  setResourceDataForPracticeQ,
  setPracticeQExpandedItems,
  setPracticeQSelectedItemId,
} = practiceQSidebarSlice.actions;
export default practiceQSidebarSlice.reducer;
